import { HttpStatusCode } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, Store, createAction, createReducer, createSelector, on, props } from '@ngrx/store';
import { UiStateActions } from '@ra-state';
import { CommandRequestBuilderService } from '@servicesV2/command-request-builder.service';
import { ApiEndPoints } from '@servicesV2/data.service';
import { LoggerService } from '@servicesV2/logger.service';
import { Observable, combineLatest, of, switchMap } from 'rxjs';
import { AppState, ResourceRoleAction } from '../lemans-app.model';
import { TENANT_MGMT_ROUTES } from '../user-data/user.model';
import {
  AccessRequestEvent,
  JoinTenant,
  handleAccessRequestApprovalErrors,
  handleCreateAccessRequestErrors,
} from './access-request.domain';

export type AccessRequestState = {
  refreshGrid: boolean;
  accessRequestCreated: boolean;
};

export type ApproveUserRole = {
  accessRequests: ResourceRoleAction[];
  action: string;
};
export const accessRequestState: AccessRequestState = {
  refreshGrid: false,
  accessRequestCreated: false,
};

export const joinTenant = createAction(
  '[APPROVE USER] Submit Request',
  props<{ payload: JoinTenant; isRedirectToDashboard: boolean }>(),
);
export const joinTenantSuccess = createAction('[APPROVE USER] Request Submitted');
export const approveUser = createAction(
  '[APPROVE USER] Approve User',
  props<{ payload: ResourceRoleAction; accessRequestId: string; tenantId: string }>(),
);
export const approveUserSuccess = createAction('[APPROVE USER] Approve User Success');

export const dismissUser = createAction('[APPROVE USER] Dismiss User', props<{ value: string; tenantId: string }>());
export const dismissUserSuccess = createAction('[APPROVE USER] Dismiss User Success');
export const resetApproveUserGrid = createAction('[APPROVE USER] Reset Grid');
export const AccessRequestActions = {
  joinTenant,
  approveUser,
  dismissUser,
  resetGrid: resetApproveUserGrid,
};

export const selectJoinOrgSuccess = createSelector(
  (state: AppState) => state.accessRequests,
  (state: AccessRequestState) => state.accessRequestCreated,
);
export const selectRefreshGridData = createSelector(
  (state: AppState) => state.accessRequests,
  (state: AccessRequestState) => state.refreshGrid,
);

const accessRequestsReducerImpl = createReducer(
  accessRequestState,
  on(joinTenantSuccess, (state): AccessRequestState => {
    return { ...state, accessRequestCreated: true };
  }),
  on(approveUserSuccess, (state): AccessRequestState => {
    return { ...state, refreshGrid: !state.refreshGrid };
  }),
  on(dismissUserSuccess, (state): AccessRequestState => {
    return { ...state, refreshGrid: !state.refreshGrid };
  }),
  on(resetApproveUserGrid, (state): AccessRequestState => {
    return { ...state, refreshGrid: false };
  }),
);
export function accessRequestReducer(state: AccessRequestState, action: Action): AccessRequestState {
  return accessRequestsReducerImpl(state, action);
}

@Injectable({
  providedIn: 'root',
})
export class AccessRequestsFacade {
  private readonly _store$!: Store<AppState>;
  private _joinOrgSuccess$?: Observable<boolean>;
  private _refresh$?: Observable<boolean>;

  // eslint-disable-next-line @angular-eslint/prefer-inject
  constructor(store$?: Store<AppState>) {
    this._store$ = store$ ?? inject<Store<AppState>>(Store);
  }

  private get store$(): Store<AppState> {
    return this._store$;
  }

  get joinOrgSuccess$(): Observable<boolean> {
    // eslint-disable-next-line sonarjs/no-nested-assignment
    return (this._joinOrgSuccess$ ??= this.store$.select(selectJoinOrgSuccess));
  }

  get refresh$(): Observable<boolean> {
    // eslint-disable-next-line sonarjs/no-nested-assignment
    return (this._refresh$ ??= this.store$.select(selectRefreshGridData));
  }

  approveUser(payload: ResourceRoleAction, accessRequestId: string, tenantId: string): void {
    this.store$.dispatch(approveUser({ accessRequestId: accessRequestId, payload: payload, tenantId: tenantId }));
  }
  dismissUser(userId: string, tenantId: string): void {
    this.store$.dispatch(dismissUser({ tenantId: tenantId, value: userId }));
  }

  joinTenant(tenant: JoinTenant, isRedirectToDashboard: boolean = true): void {
    this.store$.dispatch(joinTenant({ payload: tenant, isRedirectToDashboard }));
  }
}

@Injectable({
  providedIn: 'root',
})
export class AccessRequestsEffects {
  private readonly actions$ = inject(Actions);
  private readonly commandRequestBuilderService = inject(CommandRequestBuilderService);
  private readonly router = inject(Router);

  approveUser$ = createEffect((): Observable<any> => {
    return this.actions$.pipe(
      ofType(approveUser),
      switchMap((approveUser) => {
        return this.commandRequestBuilderService
          .new(
            ApiEndPoints.AccessRequest + '/' + approveUser.accessRequestId + '/approve',
            'POST',
            AccessRequestEvent.Approved,
          )
          .withTenantHeader(approveUser.tenantId)
          .withBody({ role: approveUser.payload.role })
          .withWaitOn200Ok()
          .withErrorHandler({
            [HttpStatusCode.BadRequest]: handleAccessRequestApprovalErrors,
          })
          .invoke$();
      }),
      switchMap((response) => {
        if (response.error) {
          return response.errorActions || [];
        }
        return [
          approveUserSuccess(),
          UiStateActions.displayMessage({
            payload: {
              message: 'Access has been granted.',
              type: 'Success',
            },
          }),
          resetApproveUserGrid(),
        ];
      }),
    );
  });

  dismissUser$ = createEffect((): Observable<any> => {
    return this.actions$.pipe(
      ofType(dismissUser),
      switchMap((removeData) => {
        return this.commandRequestBuilderService
          .new(ApiEndPoints.AccessRequest + '/' + removeData.value, 'DELETE', 'Deleted')
          .withTenantHeader(removeData.tenantId)
          .withWaitOn200Ok()
          .withErrorHandler({
            [HttpStatusCode.BadRequest]: handleAccessRequestApprovalErrors,
          })
          .invoke$();
      }),
      switchMap((response) => {
        if (response.error) {
          return response.errorActions || [];
        }
        return [
          dismissUserSuccess(),
          UiStateActions.displayMessage({
            payload: {
              message: 'Access request dismissed.',
              type: 'Info',
            },
          }),
          resetApproveUserGrid(),
        ];
      }),
    );
  });

  joinTenant$ = createEffect((): Observable<any> => {
    return this.actions$.pipe(
      ofType(joinTenant),
      switchMap((payload) => {
        const commandRequest = this.commandRequestBuilderService
          .new(`${ApiEndPoints.AccessRequest}`, 'POST', AccessRequestEvent.AccessRequestCreated)
          .withBody(payload.payload.requestBody)
          .withContext({
            tenantName: payload.payload.toastBody?.tenantName,
            resourceName: payload.payload.toastBody?.resourceName,
          })
          .withErrorHandler({
            [HttpStatusCode.BadRequest]: handleCreateAccessRequestErrors,
          });
        return combineLatest([commandRequest.invoke$(), of(payload)]);
      }),
      switchMap(([response, payload]) => {
        if (response.error && response.errorActions) {
          return response.errorActions;
        }

        if (response && payload.isRedirectToDashboard !== false) {
          this.router.navigate([TENANT_MGMT_ROUTES.ORGANIZATIONS]);
        }
        return [
          joinTenantSuccess(),
          UiStateActions.displayMessage({
            payload: {
              message: 'Request sent successfully. You will be notified once your request is approved.',
              type: 'Success',
            },
          }),
        ];
      }),
    );
  });
  logger: LoggerService;
  constructor() {
    const logger = inject(LoggerService);

    this.logger = logger.withContext('AccessRequestsEffects');
  }
}
